import { defineStore } from "pinia";
import type { Component } from "vue";
import { computed, ref, watch } from "vue";
import type { LocationQueryRaw, RouteLocationNormalized } from "vue-router";
import { useRoute } from "vue-router";

import type { ModalName } from "@/components/modal/modals";
import { loadModal } from "@/components/modal/modals";
import { getRouter } from "@/router";
import { setModalOpen } from "@/utils/shortcut/shortcuts";
import type { UnknownRecord } from "@/utils/type";
import { asReadonly } from "@/utils/vue";
import {
  deserializeObject,
  firstQueryParam,
  removePropsWithPrefix,
  serializeObject,
} from "@/utils/vue/queryParam";

interface Modal {
  component: Component;
  name: ModalName;
  attrs: UnknownRecord;
  openedAt: number;
}

const QUERY_PREFIX = "modal";

export function loadRouteModal(
  to: RouteLocationNormalized,
): Promise<Modal | undefined> | undefined {
  const name = firstQueryParam(to.query, QUERY_PREFIX);
  if (name) {
    const attrs = deserializeObject(to.query, QUERY_PREFIX);
    return loadModal(name).then(
      (component) =>
        component && { component, name: name as ModalName, attrs, openedAt: Date.now() },
    );
  }
}

export function queryWithoutModal(query: LocationQueryRaw) {
  return removePropsWithPrefix(query, QUERY_PREFIX);
}

export const useModalStore = defineStore("modal", () => {
  const modal = ref<Modal>();

  const route = useRoute();

  function updateQuery(name: ModalName, props: UnknownRecord) {
    void getRouter().syncReplace(() => ({
      query: { ...route.query, modal: name, ...serializeObject(props, QUERY_PREFIX) },
    }));
  }

  // only the latest route change may apply its loaded modal, earlier loads are stale
  let latest = 0;

  watch(route, async (route) => {
    const version = ++latest;
    const loading = loadRouteModal(route);
    if (!loading) {
      if (modal.value) {
        modal.value = undefined;
        setModalOpen(false);
      }
      return;
    }
    const loaded = await loading;
    if (version !== latest || !loaded) return;
    modal.value = loaded;
    setModalOpen(true);
  });

  return {
    modal: asReadonly(modal),

    wasModalJustOpened: computed(() => !!modal.value && Date.now() - modal.value.openedAt < 100),

    isModalOpen: (name?: ModalName) => !!modal.value && (!name || modal.value.name === name),
    open: (name: ModalName, options?: { attrs?: UnknownRecord }) =>
      updateQuery(name, options?.attrs || {}),
    close: () => void getRouter().syncReplace(() => ({ query: queryWithoutModal(route.query) })),
    changeAttrs: (attrs: UnknownRecord) => {
      if (modal.value) {
        updateQuery(modal.value.name, { ...modal.value.attrs, ...attrs });
      }
    },
  };
});
